/**
 * The family's two selectable axes, its geometry, and every string a reader can
 * see, in one place so four surfaces cannot state one fact four ways.
 *
 * The card axis is structural — the label carries the card, the photograph
 * carries it, or nothing but the macro figures do. The serving axis is
 * editorial: three captured food rows are measured `Per 101g`, `Per 100g` and
 * `Per 4 oz`, so the only real question is whether every figure states its own
 * basis or whether the set's frame line says it once.
 *
 * The nutrient labels and units below MIRROR the vendor lane's own roster
 * (`serpers/providers/fatsecret/fatsecret.renderers.ts`) rather than being
 * re-derived: the same figure has to be spelled the same way on a model-facing
 * surface and on this one, or two readers of the same capture disagree.
 *
 * The geometry constants are consequences of one measurement, taken with `curl`
 * and `ffprobe` before anything in this folder was drawn. The measurement is
 * written out in the Geometry section below.
 */
/**
 * The family enum arrives from the kit's own variants LEAF rather than its
 * barrel, and the type from the barrel as usual: the barrel re-exports a
 * surface that imports a stylesheet, and `cnutritioncard.model.ts` reads this
 * module's copy while running under `node --import tsx --test`, a runtime with
 * no CSS loader. The same split is already load-bearing in `Cwebresultcard`.
 */
import { CresultcardVariantFamily } from "@/components/ui/Cresultcard/cresultcard.variants";
import type { CresultcardVariantGroup } from "@/components/ui/Cresultcard";
import {
  CnutritioncardKind,
  CnutritioncardMetaSlot,
  CnutritioncardMicroBasis,
  CnutritioncardMicronutrient,
  CnutritioncardServingScope,
  CnutritioncardVariant,
} from "./cnutritioncard.types";

export const C_NUTRITION_CARD_VARIANTS: readonly CnutritioncardVariant[] = [
  CnutritioncardVariant.LabelLine,
  CnutritioncardVariant.PlateCard,
  CnutritioncardVariant.MacroBoard,
];

/**
 * Label line leads, and the capture is why.
 *
 * Half the corpus has no picture at all. The food endpoint publishes no image
 * field on any row — three of the six captured rows — so a media-led default
 * opens on a designed absence for every food a reader searches. What separates
 * one row from another here is the name, the brand and the energy figure, none
 * of which is a photograph; and the food rows also carry the vendor's own
 * unit-bound summary line, which is the densest true sentence on this pillar.
 */
export const DEFAULT_C_NUTRITION_CARD_VARIANT = CnutritioncardVariant.LabelLine;

export const C_NUTRITION_CARD_VARIANT_GROUP: CresultcardVariantGroup = {
  key: "pillar-widgets-nutrition-card",
  family: CresultcardVariantFamily.MediaAndCulture,
  label: "Nutrition cards",
  options: C_NUTRITION_CARD_VARIANTS,
  defaultValue: DEFAULT_C_NUTRITION_CARD_VARIANT,
};

export const C_NUTRITION_SERVING_SCOPES: readonly CnutritioncardServingScope[] =
  [CnutritioncardServingScope.Stated, CnutritioncardServingScope.FrameOnly];

/**
 * Stated leads.
 *
 * On a merged list the same food appears at three bases — the captured chicken
 * rows are `Per 101g`, `Per 100g` and `Per 4 oz` — and 197, 110 and 90 are not
 * comparable numbers until the basis is beside them. A calorie figure without
 * its serving is the one number on this pillar a reader can act on wrongly.
 */
export const DEFAULT_C_NUTRITION_SERVING_SCOPE =
  CnutritioncardServingScope.Stated;

export const C_NUTRITION_SERVING_SCOPE_GROUP: CresultcardVariantGroup = {
  key: "pillar-widgets-nutrition-serving",
  family: CresultcardVariantFamily.MediaAndCulture,
  label: "Nutrition serving scope",
  options: C_NUTRITION_SERVING_SCOPES,
  defaultValue: DEFAULT_C_NUTRITION_SERVING_SCOPE,
};


/** The row's and the rail card's own names in the variant stamp. */
export const C_NUTRITION_ROW_VARIANT = "Nutrition row";
export const C_NUTRITION_RAIL_VARIANT = "Nutrition rail card";

/** The BEM modifier each composition stamps beside the family root class. */
export const C_NUTRITION_CARD_MODIFIER: Record<CnutritioncardVariant, string> =
  {
    [CnutritioncardVariant.LabelLine]: "cnutritioncard--label",
    [CnutritioncardVariant.PlateCard]: "cnutritioncard--plate",
    [CnutritioncardVariant.MacroBoard]: "cnutritioncard--board",
  };

export const C_NUTRITION_RAIL_MODIFIER = "cnutritioncard--rail";

/* ── Geometry ──────────────────────────────────────────────────────────────
 * PROBED 2026-08-28 with `curl` and `ffprobe`, before any of this was drawn.
 * All four captured recipe assets, one request each.
 *
 * Three findings decided every frame in this folder.
 *
 * 1. EVERY asset is a 300-pixel proxy: 300×199, 300×226, 300×300 and 300×300.
 *    So no frame this family draws may be wider than 300, or the kit's own
 *    no-upscale ceiling leaves bare plate around a shrunken picture — and with
 *    the frame capped at the asset's own long edge, a `cover` crop can never
 *    upscale either.
 *
 * 2. There is no recipe aspect ratio. The four ratios run 1.000 to 1.508, so a
 *    frame that adopted each asset's own shape would draw a ragged grid of
 *    cards. A plated dish is a PHOTOGRAPH — the subject is centred and the
 *    background is part of the picture — so the frame is a fixed shape and the
 *    asset is cropped into it, which is the opposite of the rule a product
 *    cut-out follows.
 *
 * 3. The food endpoint publishes NO image field at all. Three of the six
 *    captured rows are therefore permanently picture-less, and the designed
 *    absence is the composition half this pillar renders rather than an edge
 *    case.
 */

/** The measured long edge of every captured asset. No frame may exceed it. */
export const C_NUTRITION_ASSET_EDGE = 300;

/**
 * The same ceiling as a WIDTH, which is a different thing from a number: the
 * detail is also a set's drill pane, and in the Cards layout that pane is the
 * card's own cell rather than the column the cap was chosen against.
 */
export const C_NUTRITION_DETAIL_MEDIA_WIDTH = `min(${String(C_NUTRITION_ASSET_EDGE)}px, 100%)`;

/** The lead square on a text-first card. Well under the 300px asset floor. */
export const C_NUTRITION_CARD_LEAD_MEDIA = 52;
/** The row's lead square. */
export const C_NUTRITION_ROW_MEDIA = 44;
/** The deck card's own frame: 236px of rail card less the shell's 3px bezel. */
export const C_NUTRITION_RAIL_MEDIA = 230;
/** A ceiling, not a reservation: a shorter figure gives the room back. */
export const C_NUTRITION_ROW_ENERGY_MAX = 96;
/** Between the energy figure and the open hint, inside the trailing zone. */
export const C_NUTRITION_ROW_FIGURE_GAP = 12;

/** How many chips a card shows before the row's own overflow control. */
export const C_NUTRITION_CHIP_LIMIT = 4;
/** Past four, an ingredient list on a bounded card becomes a recipe page. */
export const C_NUTRITION_CARD_INGREDIENT_LIMIT = 4;

/* ── Copy ──────────────────────────────────────────────────────────────────
 * Every reader-facing string this family authors. Everything else on the
 * surface is the vendor's own words.
 */

export const CNUTRITIONCARD_FOOD_LABEL = "Food";
export const CNUTRITIONCARD_RECIPE_LABEL = "Recipe";
export const CNUTRITIONCARD_ENERGY_LABEL = "Calories";
export const CNUTRITIONCARD_ENERGY_UNIT = "kcal";
export const CNUTRITIONCARD_SERVINGS_TITLE = "Servings";
export const CNUTRITIONCARD_INGREDIENTS_TITLE = "Ingredients";
export const CNUTRITIONCARD_DIRECTIONS_TITLE = "Directions";
export const CNUTRITIONCARD_MICRONUTRIENTS_TITLE = "Micronutrients";
export const CNUTRITIONCARD_MACROS_TITLE = "Per serving";
export const CNUTRITIONCARD_OPEN_LABEL = "Open on FatSecret";
export const CNUTRITIONCARD_SEE_ALL_CATEGORIES_LABEL = "Show every category";
export const CNUTRITIONCARD_SERVING_PREFIX = "per";
export const CNUTRITIONCARD_PREP_LABEL = "prep";
export const CNUTRITIONCARD_COOK_LABEL = "cook";
export const CNUTRITIONCARD_MINUTES_UNIT = "min";
export const CNUTRITIONCARD_YIELD_SUFFIX = "servings";
export const CNUTRITIONCARD_SERVING_NOUN = "serving";
export const CNUTRITIONCARD_SERVINGS_NOUN = "servings";
export const CNUTRITIONCARD_INGREDIENT_WITHHELD_SUFFIX = "in the opened pane";
export const CNUTRITIONCARD_INGREDIENT_NOUN = "ingredient";
export const CNUTRITIONCARD_INGREDIENTS_NOUN = "ingredients";

/** The pillar label each kind answers to, so one enum decides both surfaces. */
export const C_NUTRITION_KIND_LABEL: Record<CnutritioncardKind, string> = {
  [CnutritioncardKind.Food]: CNUTRITIONCARD_FOOD_LABEL,
  [CnutritioncardKind.Recipe]: CNUTRITIONCARD_RECIPE_LABEL,
};

/* ── The nutrient roster ───────────────────────────────────────────────────
 * Mirrored field-for-field from the vendor lane's own prose spec, labels and
 * units included.
 */

export const CNUTRITION_UNIT_GRAMS = "g";
export const CNUTRITION_UNIT_MILLIGRAMS = "mg";
export const CNUTRITION_UNIT_MICROGRAMS = "mcg";
export const CNUTRITION_UNIT_DAILY_PERCENT = "% of daily intake";

/** The four macro figures every headline surface shows, in the vendor's order. */
export const CNUTRITIONCARD_CARBOHYDRATE_LABEL = "Carbohydrate";
export const CNUTRITIONCARD_FAT_LABEL = "Fat";
export const CNUTRITIONCARD_PROTEIN_LABEL = "Protein";

/** Every non-macro figure the detail's panel prints, label and unit together. */
export const CNUTRITIONCARD_SATURATED_FAT_LABEL = "Saturated fat";
export const CNUTRITIONCARD_TRANS_FAT_LABEL = "Trans fat";
export const CNUTRITIONCARD_POLY_FAT_LABEL = "Polyunsaturated fat";
export const CNUTRITIONCARD_MONO_FAT_LABEL = "Monounsaturated fat";
export const CNUTRITIONCARD_CHOLESTEROL_LABEL = "Cholesterol";
export const CNUTRITIONCARD_SODIUM_LABEL = "Sodium";
export const CNUTRITIONCARD_POTASSIUM_LABEL = "Potassium";
export const CNUTRITIONCARD_FIBER_LABEL = "Fiber";
export const CNUTRITIONCARD_SUGAR_LABEL = "Sugar";
export const CNUTRITIONCARD_ADDED_SUGARS_LABEL = "Added sugars";

/** The four micronutrients, spelled once for both bases. */
export const C_NUTRITION_MICRO_LABEL: Record<
  CnutritioncardMicronutrient,
  string
> = {
  [CnutritioncardMicronutrient.VitaminA]: "Vitamin A",
  [CnutritioncardMicronutrient.VitaminC]: "Vitamin C",
  [CnutritioncardMicronutrient.Calcium]: "Calcium",
  [CnutritioncardMicronutrient.Iron]: "Iron",
};

/**
 * The unit each micronutrient is measured in, PER BASIS.
 *
 * This table is the whole reason the basis rides the panel. Vitamin A is the
 * one figure whose metric unit differs from its three neighbours, and every one
 * of the four changes meaning entirely under the recipe endpoint.
 */
export const C_NUTRITION_MICRO_UNIT: Record<
  CnutritioncardMicroBasis,
  Record<CnutritioncardMicronutrient, string>
> = {
  [CnutritioncardMicroBasis.MetricAmount]: {
    [CnutritioncardMicronutrient.VitaminA]: CNUTRITION_UNIT_MICROGRAMS,
    [CnutritioncardMicronutrient.VitaminC]: CNUTRITION_UNIT_MILLIGRAMS,
    [CnutritioncardMicronutrient.Calcium]: CNUTRITION_UNIT_MILLIGRAMS,
    [CnutritioncardMicronutrient.Iron]: CNUTRITION_UNIT_MILLIGRAMS,
  },
  [CnutritioncardMicroBasis.DailyPercent]: {
    [CnutritioncardMicronutrient.VitaminA]: CNUTRITION_UNIT_DAILY_PERCENT,
    [CnutritioncardMicronutrient.VitaminC]: CNUTRITION_UNIT_DAILY_PERCENT,
    [CnutritioncardMicronutrient.Calcium]: CNUTRITION_UNIT_DAILY_PERCENT,
    [CnutritioncardMicronutrient.Iron]: CNUTRITION_UNIT_DAILY_PERCENT,
  },
};

/** What the panel says its four figures are measured against, in its own words. */
export const C_NUTRITION_MICRO_BASIS_NOTE: Record<
  CnutritioncardMicroBasis,
  string
> = {
  [CnutritioncardMicroBasis.MetricAmount]:
    "Metric amounts, as the food endpoint measures them.",
  [CnutritioncardMicroBasis.DailyPercent]:
    "Percentages of daily intake, as the recipe endpoint measures them - not metric amounts.",
};

/** The declared drop order for the row's meta line, tail first. */
export const C_NUTRITION_ROW_DROP_ORDER: readonly string[] = [
  CnutritioncardMetaSlot.Time,
  CnutritioncardMetaSlot.Yield,
  CnutritioncardMetaSlot.Kind,
  CnutritioncardMetaSlot.Brand,
];
